import { ChevronDownIcon, LockIcon, SearchIcon, UsersIcon } from 'components/icons';
import { ShareBody, ShareFooter, ShareNote, ShareSection } from 'components/share/ShareDialog';
import { Button } from 'components/ui/button';
import {
    Combobox,
    ComboboxContent,
    ComboboxEmpty,
    ComboboxInput,
    ComboboxItem,
    ComboboxList,
} from 'components/ui/combobox';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { InputGroupAddon } from 'components/ui/input-group';
import { ENTITY_LINK_CLASS } from 'pages/matters/util';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { nameInitials, useEmployeeNameFormatter } from 'utils/nameOrder';

export type DraftRight = 'VIEW' | 'EDIT' | 'OWNER';

/** A person or a team a draft can be shared with. */
export type DraftShareTarget = {
    type: 'EMPLOYEE' | 'TEAM';
    id: number;
    name: string;
    /** A person's name parts, for the name shown last name first under LAST_NAME_FIRST; `null` for a team. */
    firstName?: string | null;
    lastName?: string | null;
    /** A person's core team today. */
    coreTeamName?: string | null;
    /** How many people a team has today: its members are read when the draft is opened, not when it is shared. */
    memberCount?: number | null;
};

export type DraftShare = DraftShareTarget & { right: DraftRight };

/** Each access, in the order the menu offers them, with what it allows. */
const RIGHTS: Record<DraftRight, { label: string; about: string }> = {
    VIEW: { label: 'draft.share.view', about: 'draft.share.view-desc' },
    EDIT: { label: 'draft.share.edit', about: 'draft.share.edit-desc' },
    OWNER: { label: 'draft.share.owner', about: 'draft.share.owner-desc' },
};

const ROW_CLASS = 'border-divider-soft flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b py-2.5 last:border-b-0';
const NAME_LINK_CLASS = cn(ENTITY_LINK_CLASS, 'max-w-full truncate');

type DraftShareBodyProps = {
    /** Who created the draft: its owner for good, the one row nobody changes. */
    creator: Pick<DraftShareTarget, 'id' | 'name' | 'firstName' | 'lastName'> & { isYou: boolean };
    /** Whom the draft is shared with as it is stored. The body edits its own copy from then on, so mount it once they are in. */
    shares: DraftShare[];
    /** The people and teams that match what is typed in the picker, as the caller found them. */
    targets: DraftShareTarget[];
    /** What is typed in the picker, at each keystroke: the caller debounces it before it looks targets up. */
    onSearchChange: (search: string) => void;
    /** The kind of record the reader needs to be able to see, in the plural: "cases". */
    records: string;
    saving?: boolean;
    /** Every share the draft is to have, the ones left as they were included. */
    onSubmit: (shares: DraftShare[]) => void;
    onClose: () => void;
};

/**
 * Who a draft is shared with and how far, under the header of a `ShareDialog`: a picker that adds people and teams
 * at the access chosen beside it, the list of who has access with each one's own, and the footer that saves it.
 * Only an owner opens it.
 */
export function DraftShareBody({
    creator,
    shares: stored,
    targets,
    onSearchChange,
    records,
    saving,
    onSubmit,
    onClose,
}: DraftShareBodyProps) {
    const addId = useId();
    const [shares, setShares] = useState(stored);
    const [search, setSearch] = useState('');
    const [addRight, setAddRight] = useState<DraftRight>('VIEW');
    const formatName = useEmployeeNameFormatter();

    const isCreator = (target: DraftShareTarget) => target.type === 'EMPLOYEE' && target.id === creator.id;
    const isShared = (target: DraftShareTarget) => shares.some((share) => sameTarget(share, target));
    const offered = targets.filter((target) => !isCreator(target) && !isShared(target));
    const unchanged = shareKey(shares) === shareKey(stored);
    const creatorName = creator.isYou ? t('draft.share.you', { name: formatName(creator) }) : formatName(creator);

    function changeSearch(next: string): void {
        setSearch(next);
        onSearchChange(next);
    }

    function add(target: DraftShareTarget | null): void {
        if (!target || isCreator(target) || isShared(target)) return;
        setShares([...shares, { ...target, right: addRight }]);
        changeSearch('');
    }

    return (
        <>
            <ShareBody className="pt-1 pb-5">
                <ShareSection className="gap-2">
                    <label htmlFor={addId} className="text-body1 font-medium">
                        {t('draft.share.add')}
                    </label>
                    <div className="flex flex-wrap gap-2">
                        <Combobox<DraftShareTarget>
                            items={offered}
                            value={null}
                            inputValue={search}
                            onInputValueChange={changeSearch}
                            onValueChange={add}
                            // The server filters by the typed name.
                            filter={null}
                            itemToStringLabel={formatName}
                            isItemEqualToValue={sameTarget}
                        >
                            <ComboboxInput
                                id={addId}
                                placeholder={t('draft.share.add-placeholder')}
                                showTrigger={false}
                                className="border-border-control bg-card min-w-0 flex-[1_1_14rem] shadow-none"
                            >
                                <InputGroupAddon>
                                    <SearchIcon className="text-muted-foreground size-4" />
                                </InputGroupAddon>
                            </ComboboxInput>
                            <ComboboxContent>
                                <ComboboxEmpty>{t('no-options')}</ComboboxEmpty>
                                <ComboboxList>
                                    {(target: DraftShareTarget) => (
                                        <ComboboxItem key={`${target.type}-${target.id}`} value={target}>
                                            <TargetAvatar target={target} />
                                            <TargetName target={target} />
                                        </ComboboxItem>
                                    )}
                                </ComboboxList>
                            </ComboboxContent>
                        </Combobox>
                        <RightMenu
                            value={addRight}
                            onChange={setAddRight}
                            label={t('draft.share.add-right', { right: t(RIGHTS[addRight].label) })}
                            className="h-9"
                        />
                    </div>
                </ShareSection>

                <ShareSection className="gap-0 py-1.5">
                    <h3 className="text-label text-foreground-tertiary mt-2 mb-0.5 uppercase">
                        {t('draft.share.who')}
                    </h3>
                    <ul>
                        <li className={ROW_CLASS}>
                            <TargetAvatar target={{ ...creator, type: 'EMPLOYEE' }} />
                            <span className="flex flex-[1_1_12rem] flex-col">
                                {creator.id ? (
                                    <RouterLink
                                        to={`/employee/${creator.id}`}
                                        onClick={onClose}
                                        className={NAME_LINK_CLASS}
                                    >
                                        {creatorName}
                                    </RouterLink>
                                ) : (
                                    <span>{creatorName}</span>
                                )}
                                <span className="text-caption text-muted-foreground">{t('draft.share.creator')}</span>
                            </span>
                            <span className="text-foreground-secondary flex items-center gap-1.5 pe-2.5">
                                <LockIcon className="size-3.5" />
                                {t('draft.share.owner')}
                            </span>
                        </li>
                        {shares.map((share) => (
                            <li key={`${share.type}-${share.id}`} className={ROW_CLASS}>
                                <TargetAvatar target={share} />
                                <TargetName target={share} onFollow={onClose} />
                                <RightMenu
                                    value={share.right}
                                    onChange={(right) =>
                                        setShares(
                                            shares.map((each) => (sameTarget(each, share) ? { ...each, right } : each))
                                        )
                                    }
                                    onRemove={() => setShares(shares.filter((each) => !sameTarget(each, share)))}
                                    label={t('draft.share.access-of', {
                                        name: formatName(share),
                                        right: t(RIGHTS[share.right].label),
                                    })}
                                    className="h-8"
                                />
                            </li>
                        ))}
                    </ul>
                </ShareSection>

                <ShareNote>{t('draft.share.note', { records })}</ShareNote>
            </ShareBody>
            <ShareFooter
                submitLabel={t('draft.share.submit')}
                onCancel={onClose}
                onSubmit={() => onSubmit(shares)}
                disabled={saving || unchanged}
            />
        </>
    );
}

function sameTarget(a: DraftShareTarget, b: DraftShareTarget): boolean {
    return a.type === b.type && a.id === b.id;
}

function shareKey(shares: DraftShare[]): string {
    return shares.map((share) => `${share.type}-${share.id}-${share.right}`).join();
}

/** A person's initials, or a team's glyph. Drawn only: the name beside it says who it is. */
function TargetAvatar({ target }: { target: DraftShareTarget }) {
    return target.type === 'TEAM' ? (
        <span
            aria-hidden
            className="bg-neutral-tint text-foreground-secondary flex size-8 shrink-0 items-center justify-center rounded-full"
        >
            <UsersIcon className="size-4" />
        </span>
    ) : (
        <span
            aria-hidden
            className="bg-primary-tint text-primary-ink text-caption flex size-8 shrink-0 items-center justify-center rounded-full font-semibold"
        >
            {nameInitials(target)}
        </span>
    );
}

/**
 * Whether a target is a person, in which core team, or a team, of how many people. The dialog counts a team's people
 * "today"; a record's Shared with card words it with `teamId`.
 */
export function targetAbout(target: DraftShareTarget, teamId = 'draft.share.team'): string {
    if (target.type === 'TEAM') {
        return t(teamId, { count: String(target.memberCount ?? 0) });
    }
    return target.coreTeamName ? t('draft.share.person-in', { team: target.coreTeamName }) : t('draft.share.person');
}

/**
 * The name, and under it what `targetAbout` says of it. Given `onFollow`, the name links to the person's or the team's
 * page, and following it calls `onFollow` to close the dialog: from a draft employee or team page, the link lands on
 * the same route, whose page would keep the dialog open.
 */
function TargetName({ target, onFollow }: { target: DraftShareTarget; onFollow?: () => void }) {
    const formatName = useEmployeeNameFormatter();
    const name = formatName(target);
    return (
        <span className="flex min-w-0 flex-[1_1_12rem] flex-col">
            {onFollow ? (
                <RouterLink
                    to={target.type === 'TEAM' ? `/team/${target.id}` : `/employee/${target.id}`}
                    onClick={onFollow}
                    className={NAME_LINK_CLASS}
                >
                    {name}
                </RouterLink>
            ) : (
                <span className="truncate">{name}</span>
            )}
            <span className="text-caption text-muted-foreground">{targetAbout(target)}</span>
        </span>
    );
}

type RightMenuProps = {
    value: DraftRight;
    onChange: (right: DraftRight) => void;
    /** Offers Remove access under the rights. */
    onRemove?: () => void;
    /** Names the button with whose access it sets and what that is now. */
    label: string;
    className?: string;
};

/** A button naming an access, which opens the three with what each allows. */
function RightMenu({ value, onChange, onRemove, label, className }: RightMenuProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger
                render={
                    <Button
                        variant="outline"
                        aria-label={label}
                        className={cn(
                            'border-border-control bg-card hover:bg-card aria-expanded:border-primary aria-expanded:bg-card gap-1.5 ps-3 pe-2 font-normal shadow-none',
                            className
                        )}
                    />
                }
            >
                {t(RIGHTS[value].label)}
                <ChevronDownIcon className="text-muted-foreground size-4" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[290px]">
                <DropdownMenuRadioGroup value={value} onValueChange={(next: DraftRight) => onChange(next)}>
                    {(Object.keys(RIGHTS) as DraftRight[]).map((right) => (
                        <DropdownMenuRadioItem
                            key={right}
                            value={right}
                            closeOnClick
                            // Only the chosen right makes room for its check, so the others' descriptions keep one line.
                            className="data-checked:bg-primary-wash [&_[data-slot=dropdown-menu-radio-item-indicator]]:text-primary items-start py-2 pr-2.5 pl-2.5 data-checked:pr-8"
                        >
                            <span className="flex flex-col gap-px">
                                <span className="font-medium">{t(RIGHTS[right].label)}</span>
                                <span className="text-meta text-muted-foreground">{t(RIGHTS[right].about)}</span>
                            </span>
                        </DropdownMenuRadioItem>
                    ))}
                </DropdownMenuRadioGroup>
                {onRemove ? (
                    <>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={onRemove} className="text-danger-ink py-2 font-medium">
                            {t('draft.share.remove')}
                        </DropdownMenuItem>
                    </>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
}
